import { ChangeDetectionStrategy, Component, inject, input } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { ENVIRONMENT } from '@cfp/core';
import { Co2Module, CurrencyModule, IconSquare } from '@cfp/ui';
import { TranslateModule } from '@ngx-translate/core';
import { BuildingMeasuresService, ReportPlusBuildingMeasureDTO } from 'csr-asset-app';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

@Component({
  selector: 'cfp-building-report-explanation-measure-panel',
  templateUrl: './building-report-explanation-measure-panel.html',
  styleUrls: ['./building-report-explanation-measure-panel.scss'],
  imports: [TranslateModule, RouterLink, CurrencyModule, Co2Module, IconSquare],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BuildingReportExplanationMeasurePanel {
  readonly portfolioId = input<number | undefined>(undefined);
  readonly buildingId = input<number | undefined>(undefined);
  readonly align = input<'left' | 'right'>('left');
  readonly measure = input<ReportPlusBuildingMeasureDTO | undefined>(undefined);
  readonly showDetailsButton = input<boolean>(false);
  readonly showSummary = input<boolean>(true);
  readonly cardTitleClass = input<string | undefined>(undefined);
  readonly showBusinesscase = input<boolean>(false);

  protected measureImageUrl = toSignal(
    toObservable(this.measure).pipe(
      switchMap((measure) => {
        if (!measure) {
          return of(undefined);
        }
        return this.getImageUrl(measure);
      })
    )
  );

  protected readonly environment = inject(ENVIRONMENT);
  private readonly buildingMeasuresService = inject(BuildingMeasuresService);

  private getImageUrl(measure: ReportPlusBuildingMeasureDTO): Observable<string | undefined> {
    if (measure.measureImageId) {
      return this.loadUserDefinedImage(measure.id).pipe(
        map((url: string) => {
          return `url(${url})`;
        })
      );
    } else if (measure.code) {
      // Use default measure image
      const url = `${this.environment.measureImagesDir}${measure?.code}.jpg`;
      return of(`url(${url})`);
    }
    return of(undefined);
  }

  private loadUserDefinedImage(buildingMeasureId: number): Observable<string> {
    return this.buildingMeasuresService.v2BuildingmeasuresBuildingMeasureIdImageGet(buildingMeasureId).pipe(
      map((measureImage: any) => {
        return URL.createObjectURL(measureImage);
      })
    );
  }
}
